import React from 'react';
import {
  CpuChipIcon,
  ChartBarIcon,
  LightBulbIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';

export default function SicknessAIHelp() {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-purple-600 p-3 rounded-full">
              <CpuChipIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Sickness AI Analytics</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advanced AI-powered insights for sickness absence management, predictive analytics,
            and data-driven strategies to improve employee health and organizational productivity.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <LightBulbIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            Sickness AI leverages machine learning algorithms to analyze absence patterns,
            predict future sickness trends, and provide actionable insights for HR managers.
            Transform raw absence data into strategic intelligence that supports employee wellbeing
            and organizational success.
          </p>
        </div>

        {/* AI Capabilities */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">AI-Powered Capabilities</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <ArrowUpIcon className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-purple-800">Predictive Analytics</h3>
              </div>
              <p className="text-purple-700 mb-4">Forecast future absence trends and identify at-risk employees</p>
              <ul className="space-y-2 text-purple-600">
                <li>• Seasonal sickness patterns</li>
                <li>• Individual risk assessment</li>
                <li>• Department-level predictions</li>
                <li>• Early intervention alerts</li>
              </ul>
            </div>

            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-3" />
                <h3 className="text-xl font-semibold text-indigo-800">Pattern Recognition</h3>
              </div>
              <p className="text-indigo-700 mb-4">Identify hidden patterns in absence data</p>
              <ul className="space-y-2 text-indigo-600">
                <li>• Common absence triggers</li>
                <li>• Department correlations</li>
                <li>• Demographic insights</li>
                <li>• Workplace environment factors</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <LightBulbIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-blue-800">Smart Recommendations</h3>
              </div>
              <p className="text-blue-700 mb-4">AI-generated strategies for absence reduction</p>
              <ul className="space-y-2 text-blue-600">
                <li>• Wellness program suggestions</li>
                <li>• Policy optimization</li>
                <li>• Intervention strategies</li>
                <li>• Resource allocation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Data Analysis */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ChartBarIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Advanced Data Analysis</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-time Processing:</strong> Continuous analysis of new absence data as it's entered</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Historical Trends:</strong> Long-term pattern analysis across multiple years</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Comparative Analysis:</strong> Benchmark against industry standards and internal targets</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Root Cause Analysis:</strong> Identify underlying factors contributing to absences</span>
              </li>
            </ul>
          </div>

          {/* Predictive Features */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Predictive Intelligence</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Risk Scoring:</strong> Individual employee absence risk assessments</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Early Warning System:</strong> Alerts for potential high-absence periods</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Seasonal Forecasting:</strong> Predict peak absence periods based on historical data</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Intervention Timing:</strong> Optimal moments for preventive wellness programs</span>
              </li>
            </ul>
          </div>

          {/* Reporting & Insights */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Automated Reporting</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Executive Dashboards:</strong> High-level absence metrics for leadership</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Department Reports:</strong> Team-specific absence analysis and trends</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Compliance Reports:</strong> Regulatory reporting and audit trails</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Custom Analytics:</strong> Flexible reporting for specific business needs</span>
              </li>
            </ul>
          </div>

          {/* Wellness Integration */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Wellness Program Integration</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Targeted Interventions:</strong> Personalized wellness recommendations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Program Effectiveness:</strong> Measure impact of wellness initiatives</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Resource Optimization:</strong> Allocate wellness resources where most needed</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>ROI Tracking:</strong> Quantify benefits of health and wellness programs</span>
              </li>
            </ul>
          </div>
        </div>

        {/* AI Process Flow */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <CpuChipIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">AI Analysis Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold mb-2">Data Collection</h3>
              <p className="text-purple-100 text-sm">Gather absence data from multiple sources</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold mb-2">Pattern Analysis</h3>
              <p className="text-purple-100 text-sm">Identify trends and correlations using ML algorithms</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold mb-2">Predictive Modeling</h3>
              <p className="text-purple-100 text-sm">Generate forecasts and risk assessments</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <h3 className="font-semibold mb-2">Actionable Insights</h3>
              <p className="text-purple-100 text-sm">Deliver recommendations and automated alerts</p>
            </div>
          </div>
        </div>

        {/* Benefits & Impact */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Business Impact & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📈 Operational Benefits</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Reduced absence rates through early intervention</li>
                <li>• Optimized workforce planning and resource allocation</li>
                <li>• Decreased administrative burden on HR teams</li>
                <li>• Improved compliance with absence reporting requirements</li>
                <li>• Enhanced decision-making with data-driven insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💼 Strategic Advantages</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Proactive rather than reactive absence management</li>
                <li>• Competitive advantage through employee wellbeing focus</li>
                <li>• Cost savings from reduced absence-related expenses</li>
                <li>• Improved organizational reputation and employee satisfaction</li>
                <li>• Future-ready workforce analytics capabilities</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">👥 Employee Benefits</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Personalized support based on individual risk profiles</li>
                <li>• Targeted wellness programs addressing specific needs</li>
                <li>• Reduced stigma around health-related absences</li>
                <li>• Improved work-life balance through predictive insights</li>
                <li>• Enhanced support during difficult periods</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔧 Technical Benefits</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Automated data processing and analysis</li>
                <li>• Real-time insights and alerts</li>
                <li>• Scalable solution for growing organizations</li>
                <li>• Integration with existing HR systems</li>
                <li>• Continuous learning and model improvement</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Implementation Guidelines */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Implementation & Usage Guidelines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🚀 Getting Started</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Ensure data quality and completeness</li>
                <li>• Train HR team on AI insights interpretation</li>
                <li>• Establish baseline metrics before implementation</li>
                <li>• Set up automated reporting schedules</li>
                <li>• Define key performance indicators</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Data Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Maintain consistent data entry standards</li>
                <li>• Regularly audit data accuracy</li>
                <li>• Protect sensitive employee health information</li>
                <li>• Ensure compliance with data protection regulations</li>
                <li>• Update models with new data regularly</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Access insights on mobile devices for remote work</li>
                <li>• Receive real-time alerts on absence trends</li>
                <li>• Review predictive analytics during meetings</li>
                <li>• Share reports with stakeholders on-the-go</li>
                <li>• Monitor dashboard metrics from any location</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Best Practices</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Combine AI insights with human judgment</li>
                <li>• Focus on prevention rather than reaction</li>
                <li>• Regularly review and refine AI models</li>
                <li>• Communicate transparently with employees</li>
                <li>• Measure and track improvement over time</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Privacy & Ethics</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Obtain proper consent for data usage</li>
                <li>• Anonymize sensitive health information</li>
                <li>• Ensure ethical use of predictive analytics</li>
                <li>• Maintain transparency in AI decision-making</li>
                <li>• Regular audits of AI system fairness</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📈 Continuous Improvement</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Monitor AI accuracy and refine models</li>
                <li>• Gather feedback from HR team and employees</li>
                <li>• Update algorithms with new research findings</li>
                <li>• Expand integration with wellness programs</li>
                <li>• Track ROI and business impact metrics</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}